/**
 * Category hierarchy tree utils (V2)
 *
 * Class-only export.
 */

type UnknownRecord = Record<string, unknown>;

export interface CategoryNode {
    id: string;
    name: string;
    productsCount?: number | null;
}

export interface CategoryTree {
    parents: CategoryNode[];
    childrenByParentId: Map<string, CategoryNode[]>;
}

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export class CategoryHierarchyTreeUtils {
    public static createParentsMap(): Map<string, CategoryNode> {
        return new Map<string, CategoryNode>();
    }

    public static createChildrenByParentMap(): Map<string, CategoryNode[]> {
        return new Map<string, CategoryNode[]>();
    }

    public static fromApiData(data: unknown): CategoryTree {
        const parentsById = CategoryHierarchyTreeUtils.createParentsMap();
        const childrenByParentId = CategoryHierarchyTreeUtils.createChildrenByParentMap();

        const obj = isRecord(data) ? data : null;
        const nested = obj && Array.isArray(obj["parent_categories"]) ? obj["parent_categories"] : null;
        if (nested) {
            nested.forEach((parent) => {
                const parentObj = isRecord(parent) ? parent : null;
                const parentId = String(parentObj?.["category_id"] ?? "").trim();
                const parentName = String(parentObj?.["category_name"] ?? "").trim();
                if (!parentId || !parentName) return;

                const parentCountRaw = parentObj?.["products_count"];
                const parentCountNumber =
                    typeof parentCountRaw === "number"
                        ? parentCountRaw
                        : parentCountRaw != null
                          ? Number(parentCountRaw)
                          : null;
                const parentCount =
                    Number.isFinite(parentCountNumber) && parentCountNumber !== null
                        ? Math.max(0, Math.trunc(parentCountNumber))
                        : null;

                parentsById.set(parentId, { id: parentId, name: parentName, productsCount: parentCount });

                const children = parentObj && Array.isArray(parentObj["categories"]) ? parentObj["categories"] : [];
                children.forEach((child) => {
                    const childObj = isRecord(child) ? child : null;
                    const childId = String(childObj?.["category_id"] ?? "").trim();
                    const childName = String(childObj?.["category_name"] ?? "").trim();
                    if (!childId || !childName) return;
                    if (childId === parentId) return;

                    const childCountRaw = childObj?.["products_count"];
                    const childCountNumber =
                        typeof childCountRaw === "number"
                            ? childCountRaw
                            : childCountRaw != null
                              ? Number(childCountRaw)
                              : null;
                    const childCount =
                        Number.isFinite(childCountNumber) && childCountNumber !== null
                            ? Math.max(0, Math.trunc(childCountNumber))
                            : null;

                    if (!childrenByParentId.has(parentId)) childrenByParentId.set(parentId, []);
                    childrenByParentId.get(parentId)?.push({
                        id: childId,
                        name: childName,
                        productsCount: childCount,
                    });
                });
            });

            const parents = Array.from(parentsById.values());
            return { parents, childrenByParentId };
        }

        const flat = obj && Array.isArray(obj["categories"]) ? obj["categories"] : [];
        flat.forEach((child) => {
            const childObj = isRecord(child) ? child : null;
            const childId = String(childObj?.["category_id"] ?? "").trim();
            const childName = String(childObj?.["category_name"] ?? "").trim();
            if (!childId || !childName) return;

            const countRaw = childObj?.["products_count"];
            const countNumber = typeof countRaw === "number" ? countRaw : countRaw != null ? Number(countRaw) : null;
            const productsCount =
                Number.isFinite(countNumber) && countNumber !== null ? Math.max(0, Math.trunc(countNumber)) : null;

            const parentIdRaw = childObj?.["parent_category_id"];
            const parentId =
                parentIdRaw === null || parentIdRaw === undefined || parentIdRaw === ""
                    ? null
                    : String(parentIdRaw).trim();

            if (!parentId) {
                parentsById.set(childId, { id: childId, name: childName, productsCount });
                return;
            }

            if (!childrenByParentId.has(parentId)) childrenByParentId.set(parentId, []);
            if (childId !== parentId) {
                childrenByParentId.get(parentId)?.push({ id: childId, name: childName, productsCount });
            }
        });

        Array.from(childrenByParentId.keys()).forEach((parentId) => {
            if (!parentsById.has(parentId)) {
                parentsById.set(parentId, { id: parentId, name: "(Unknown Parent)", productsCount: null });
            }
        });

        const parents = Array.from(parentsById.values());
        return { parents, childrenByParentId };
    }

    public static fromEmbedded(tree: unknown): CategoryTree {
        const parentsById = CategoryHierarchyTreeUtils.createParentsMap();
        const childrenByParentId = CategoryHierarchyTreeUtils.createChildrenByParentMap();

        const obj = isRecord(tree) ? tree : null;
        const parents = obj && Array.isArray(obj["parents"]) ? obj["parents"] : [];
        parents.forEach((parent) => {
            const parentObj = isRecord(parent) ? parent : null;
            const id = String(parentObj?.["id"] ?? "").trim();
            const name = String(parentObj?.["name"] ?? "").trim();
            if (!id || !name) return;
            parentsById.set(id, { id, name });
        });

        const childrenByParent = obj ? obj["children_by_parent"] : null;
        const record = isRecord(childrenByParent) ? childrenByParent : {};
        Object.entries(record).forEach(([parentIdRaw, list]) => {
            const parentId = String(parentIdRaw ?? "").trim();
            if (!parentId) return;

            const children = Array.isArray(list) ? list : [];
            children.forEach((child) => {
                const childObj = isRecord(child) ? child : null;
                const childId = String(childObj?.["id"] ?? "").trim();
                const childName = String(childObj?.["name"] ?? "").trim();
                if (!childId || !childName) return;
                if (childId === parentId) return;

                if (!childrenByParentId.has(parentId)) childrenByParentId.set(parentId, []);
                childrenByParentId.get(parentId)?.push({ id: childId, name: childName });
            });

            if (!parentsById.has(parentId)) {
                parentsById.set(parentId, { id: parentId, name: "(Unknown Parent)" });
            }
        });

        return { parents: Array.from(parentsById.values()), childrenByParentId };
    }
}
